import React from 'react';
import LazyLoad from 'react-lazyload';
import config from '../LazyLoadComponents/config';
import { createStyledImg, generateSizesString, generateSrcSetString, Interpolations } from './helpers';

/* Types */
type ImgSource = string;
type SizesWidth = string;

export interface ResponsiveImgSource {
  src: ImgSource;
  width: number;
}

export interface ResponsiveImgSize {
  mediaQuery: string;
  width: SizesWidth;
}

export interface ResponsivenessConfig {
  fallbackSize: {
    width: SizesWidth;
  };
  imgSources: {
    fallbackSource: ResponsiveImgSource;
    responsiveSources: ResponsiveImgSource[];
  };
  sizes?: ResponsiveImgSize[];
}

export interface LazyLoadingConfig {
  placeholderHeight?: string;
  placeholderWidth?: string;
}

export interface ResponsiveImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  lazyLoadingConfig?: LazyLoadingConfig;
  responsivenessConfig?: ResponsivenessConfig;
  src: ImgSource;
}

/* Components */
/* eslint-disable-next-line prefer-arrow/prefer-arrow-functions */
const StyledResponsiveImg = function<T extends object = {}>(
  templateStringsArray: TemplateStringsArray,
  ...interpolations: Interpolations
): React.FC<ResponsiveImgProps & T> {
  return props => {
    const { lazyLoadingConfig, src, responsivenessConfig, ...restProps } = props;

    let sizes: string;
    let srcSet: string;
    if (responsivenessConfig) {
      const {
        imgSources: { fallbackSource, responsiveSources },
      } = responsivenessConfig;
      sizes = generateSizesString(responsivenessConfig);
      srcSet = generateSrcSetString(responsiveSources.concat(fallbackSource));
    }

    const imgProps = { ...restProps, sizes, src, srcSet };

    if (!lazyLoadingConfig) {
      return <>{createStyledImg<T>(imgProps, templateStringsArray, interpolations)}</>;
    }

    const { placeholderHeight, placeholderWidth } = lazyLoadingConfig;

    return (
      <LazyLoad height={placeholderHeight} width={placeholderWidth} {...config.img}>
        {createStyledImg<T>(imgProps, templateStringsArray, interpolations)}
      </LazyLoad>
    );
  };
};
export default StyledResponsiveImg;
